import { FeatureType, IntegrationException } from '../../types';
/* eslint-disable no-template-curly-in-string */
import { LitElement, PropertyValueMap, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';

import { StateController } from '../../helpers/state/state-management/state-controller';
import accountInfo from '../../helpers/state/account-info';
import pointCache from '../../helpers/state/point-cache';
import topicData from '../../helpers/state/topic-data';

interface SettingsButton {
	icon: string;
	label: string;
	newButtonSet?: SettingControlsType;
}

export enum SettingControlsType {
	DEFAULT = 'default',
	MAP_STYLES = 'map-styles',
	MAP_SETTINGS = 'map-settings',
	ADD_POINT = 'add-point',
}

@customElement('setting-control-buttons')
export class SettingControlButtons extends LitElement {
	@property()
	buttonType: SettingControlsType = SettingControlsType.DEFAULT;

	@state()
	buttonsToRender: SettingsButton[] = [];

	@state()
	activeButton?: SettingsButton;

	private readonly bindAccountState = new StateController(this, accountInfo);

	buttonTypeToButtonsMap: Map<SettingControlsType, SettingsButton[]> = new Map([
	  [SettingControlsType.DEFAULT,
		topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations ? [] :
		[
		  { icon: 'brush', label: 'Map Style', newButtonSet: SettingControlsType.MAP_STYLES },
		  { icon: 'category', label: 'Categories & Tags' },
		  { icon: 'settings', label: 'Advanced' },
		  { icon: 'language', label: 'Publish to Proxi', },
		]
	  ],
	  [SettingControlsType.MAP_STYLES,
		[
		  { icon: 'title', label: 'Rename Map' },
		  { icon: 'format_color_fill', label: 'Header Colors' },
		  { icon: 'crop_original', label: 'Header Logo' },
		  { icon: 'auto_awesome', label: 'Map Style' },
		  { icon: 'trip_origin', label: `Icon Settings` },
		]
	  ],
	  [SettingControlsType.ADD_POINT,
		[
		  { icon: 'location_on', label: 'Location' },
		  { icon: 'notes', label: 'Title & Details' },
		  { icon: 'crop_original', label: 'Images' },
		  { icon: 'sell', label: 'Tags' },
		  { icon: 'link', label: 'Link Outs' },
		  { icon: 'card_giftcard', label: 'Special Offer' },
		]
	  ]
	]);

	addPointButton: SettingsButton = {
		icon: 'add_location',
		label: `Add Point`,
	};

	handleUpgradeClick(featureType: FeatureType) {
		this.dispatchEvent(
			new CustomEvent('on-upgrade-click', {
				detail: {
					featureType
				}
			})
		);
	}

	protected updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
		this.buttonsToRender = this.buttonTypeToButtonsMap.get(this.buttonType) ?? [];
	}

	handleButtonClick(button: SettingsButton) {
		if (button.newButtonSet) {
			this.buttonType = button.newButtonSet;
		} else {
			this.activeButton = button;
			this.dispatchEvent(
				new CustomEvent('on-button-click', {
					detail: {
						label: button.label
					}
				})
			);
		}
	}

	resetActiveButton() {
		this.activeButton = undefined;
	}

	save() {
		this.dispatchEvent(new CustomEvent('on-save'));
	}

	render() {
		return html`
			<div class="setting-controls-button__wrapper">
				<div class="setting-controls-button__scroll-container">
					<div class="stickyButton">
					${this.buttonType !== SettingControlsType.DEFAULT ?
						html`<div class="setting-controls-button setting-drawer-button--back" @click=${() => { this.buttonType = SettingControlsType.DEFAULT; } }>
								<mwc-icon class="setting-controls-button__icon">arrow_back</mwc-icon>
								<span class="setting-controls-button__label">Back</span>
					</div>`
					: html `
						<div class="setting-controls-button setting-drawer-button--back ${this.addPointButton === this.activeButton ? 'setting-controls-button--active' : ''}" @click=${() => pointCache.pointsRemaining <= 0 ? this.handleUpgradeClick(FeatureType.PointsPerMap) : this.handleButtonClick(this.addPointButton)}>
								<mwc-icon class="setting-controls-button__icon" style="color: var(--main-success-color)">add_location</mwc-icon>
								<span class="setting-controls-button__label">Add ${topicData.topic?.point_name ?? 'Point'}</span>
						</div>
						`
					}
					</div>

					<div class="scrollyButton">
					${this.buttonsToRender.map(button =>
						html`
							<button class="setting-controls-button ${button === this.activeButton ? 'setting-controls-button--active' : ''}" @click=${() => this.handleButtonClick(button)}>
								${button.icon.endsWith('.png') ? html`
									<img class="setting-controls-button__png" src="/images/upgrade_orange.png" />
								` : html`
									<mwc-icon class="setting-controls-button__icon">${button.icon}</mwc-icon>
								`}
								<span class="setting-controls-button__label">${button.label}</span>
							</button>
						`
					)}
					</div>
				</div>
			</div>`;
	}

	static get styles() {
		return css`

            /* width */
            .scrollyButton::-webkit-scrollbar {
                width: 2px;
            }

            /* Track */
            .scrollyButton::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .scrollyButton::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

			.setting-controls-button__wrapper {
				overflow: scroll hidden;
				padding: 4px;
				scrollbar-width: thin; // this is for firefox
				margin-bottom: -16px; // this is to make up for the scrollbar width setting
				}

			.setting-controls-button__wrapper::-webkit-scrollbar {
				width: 0px; // this makes the scrollbar virtually invisible
			}

			.setting-controls-button__scroll-container {
				display: flex;
			}

			.stickyButton {
				display: flex;
				height: 100%;
			}

			.setting-controls-button {
				border-radius: 2px;
				border: none;
				cursor: pointer;
				padding: 4px 8px;
				background: transparent;
				color: var(--main-primary-color);
				max-height: 65px;
				display: flex;
				flex-direction: column;
				align-items: center;
				--mdc-icon-size: 1rem;
				gap:5px;
				flex: 1;
				height: 100%;
				justify-content: space-around;
			}

			.scrollyButton {
				flex: 1;
				overflow: auto;
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: space-evenly;
			}

			.setting-controls-button--active {
				background-color: #fff;
			}

			.setting-drawer-button--primary {
				color: var(--main-success-color);
				--mdc-icon-size: 16px;
				border-right: 1px solid gray;
			}

			.setting-drawer-button--back {
				color: var(--main-primary-color);
				--mdc-icon-size: 16px;
				border-right: 1px solid gray;
			}

			.setting-drawer-button--add-point {
				color: var(--main-invalid-color);
				--mdc-icon-size: 16px;
				border-right: 1px solid gray;
			}

			.setting-controls-button__icon {
				margin-left: auto;
				margin-right: auto;
			}

			.setting-controls-button__png {
				margin-left: auto;
				margin-right: auto;
				height: 16px;
				width: 16px;
			}

			.setting-controls-button__label {
				color: var(--main-primary-color);
				text-align: center;
				font-size: 0.7rem;
			}
		`;
	}
}
